import React, { useState, useEffect } from "react";
import { useOne } from "@pankod/refine-core";
import {
    useModalForm, Modal, Space, Form,
    Row,
    Col,
    Button,
    Input,
    Select,
    useSelect,
    notificationProvider,
} from "@pankod/refine-antd";
import { IOptionGroup, IState, InputValidatorRegex } from "../../../../interfaces/company";

interface AddLocationAdditionalDataModelProps {
    companyId: string;
    refetchCompanyDetail?: (shouldRefetch: boolean) => void;
}

export const AddLocationAdditionalDataModel: React.FC<AddLocationAdditionalDataModelProps> = ({
    companyId,
    refetchCompanyDetail
}) => {
    const [stateId, setStateId] = useState<number | null>(null);
    const [cityList, setCityList] = useState<IOptionGroup[]>([]);
    const [isCitiesLoading, setIsCitiesLoading] = useState(false);

    // Get state list
    const { selectProps: stateSelectProps } = useSelect<IState>({
        dataProviderName: "grapghQl",
        resource: 'getAllState',
        optionLabel: "name",
        optionValue: "id"
    });
    stateSelectProps.showSearch = true;

    // Fetch cities based on selected state
    const { refetch: fetchCities } = useOne<any>({
        dataProviderName: "grapghQl",
        resource: 'getCityByState',
        id: '',
        metaData: { stateId: stateId },
        queryOptions: {
            enabled: false,
            onSuccess: (data) => {
                // setCityListLength(data.data.length);
                setCityList(data.data);
                setIsCitiesLoading(false);
            },
            onError: () => {
                setIsCitiesLoading(false);
            }
        },
    });

    useEffect(() => {
        if (stateId) {
            setIsCitiesLoading(true);
            fetchCities();
        }
    }, [stateId, fetchCities]);

    // Add Location Modal Form
    const {
        modalProps: locationModalProps,
        formProps: locationFormProps,
        show: showLocationModal,
        close: closeLocationModal,
        submit: submitForm,
    } = useModalForm<any>({
        resource: "saveSingleMasterData",
        dataProviderName: "grapghQl",
        action: "create",
        redirect: false,
        submitOnEnter: false,
        metaData: {
            companyId: companyId,
        },
        onMutationSuccess() {
            notificationProvider.open({
                message: "Successfully saved location",
                type: 'success',
                description: 'Location data has been added successfully',
            });
            if (refetchCompanyDetail) {
                refetchCompanyDetail(true);
            }
            closeLocationModal();
        },
        onMutationError(error: any) {
            notificationProvider.open({
                message: error?.message || "Failed to save location",
                type: 'error',
                description: 'Unable to save location data',
            });
        }
    });

    locationModalProps.title = 'Add Location';
    locationModalProps.centered = true;
    locationModalProps.width = 900;
    locationModalProps.onOk = async () => {
        try {
            // Validate and submit the form
            await locationFormProps.form?.validateFields();
            locationFormProps.form?.submit();
        } catch (error) {
            // Form validation failed - errors will be shown in the form
            console.error('Form validation error:', error);
        }
    };

    locationModalProps.afterClose = () => {
        locationFormProps.form?.resetFields();
        setStateId(null);
        setCityList([]);
    };

    const handleStateChange = (value: any) => {
        const stateIdValue = typeof value === 'object' ? value.value : value;
        setStateId(parseInt(stateIdValue));
        locationFormProps.form?.setFieldsValue({
            cityId: null,
        });
        setCityList([]);
    };

    const validateEmail = (_: any, value: string) => {
        if (!value) {
            return Promise.resolve();
        }
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (emailRegex.test(value)) {
            return Promise.resolve();
        }
        return Promise.reject(new Error('Please enter a valid email address'));
    };

    const validateMobileNumber = (_: any, value: string) => {
        if (!value) {
            return Promise.resolve();
        }
        if (value.length === 10 && /^[0-9]+$/.test(value)) {
            return Promise.resolve();
        }
        return Promise.reject(new Error('Please enter a valid 10-digit mobile number'));
    };

    return (
        <Space>
            <Button
                type="primary"
                onClick={() => {
                    showLocationModal();
                }}
            >
                Add Location
            </Button>

            <Modal {...locationModalProps}>
                <Form
                    {...locationFormProps}
                    layout="vertical"
                    initialValues={{
                        countryId: 1, // India by default
                    }}
                >
                    <Row gutter={16}>
                        <Col sm={12}>
                            <Form.Item
                                label="Location Name"
                                name="name"
                                rules={[
                                    { required: true, message: 'Please enter location name' },
                                    {
                                        pattern: /^[a-zA-Z0-9\s\-_.]+$/,
                                        message: 'Please enter a valid name'
                                    }
                                ]}
                            >
                                <Input placeholder="Enter location name" />
                            </Form.Item>
                        </Col>
                        <Col lg={12}>
                            <Form.Item
                                label="Branch Code"
                                name="branchCode"
                            >
                                <Input placeholder="Enter branch code" />
                            </Form.Item>
                        </Col>
                        <Col lg={12}>
                            <Form.Item
                                label="Branch Name"
                                name="branchName"
                                rules={[{ required: true, message: 'Please enter branch name' }]}
                            >
                                <Input placeholder="Enter branch name" />
                            </Form.Item>
                        </Col>
                        <Col lg={12}>
                            <Form.Item
                                label="GSTIN"
                                name="gstin"
                            >
                                <Input placeholder="Enter GSTIN" />
                            </Form.Item>
                        </Col>
                        <Col lg={12}>
                            <Form.Item
                                label="Email Address"
                                name="emailAddress"
                                rules={[{ validator: validateEmail }]}
                            >
                                <Input placeholder="Enter email address" />
                            </Form.Item>
                        </Col>
                        <Col lg={12}>
                            <Form.Item
                                label="Contact Number"
                                name="contactNumber"
                                rules={[{ validator: validateMobileNumber }]}
                            >
                                <Input
                                    placeholder="Enter contact number"
                                    maxLength={10}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                        locationFormProps.form?.setFieldsValue({ contactNumber: value });
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col lg={12}>
                            <Form.Item
                                label="Address Line 1"
                                name="addressLine1"
                                rules={[{ required: true, message: 'Please enter address' }]}
                            >
                                <Input placeholder="Enter address line 1" />
                            </Form.Item>
                        </Col>
                        <Col lg={12}>
                            <Form.Item
                                label="Address Line 2"
                                name="addressLine2"
                            >
                                <Input placeholder="Enter address line 2" />
                            </Form.Item>
                        </Col>
                        <Col lg={8}>
                            <Form.Item
                                label="State"
                                name="stateId"
                                rules={[{ required: true, message: 'Please select state' }]}
                            >
                                <Select
                                    placeholder='Select State'
                                    onChange={handleStateChange}
                                    {...stateSelectProps}
                                />
                            </Form.Item>
                        </Col>
                        <Col lg={8}>
                            <Form.Item
                                label="City"
                                name="cityId"
                                rules={[{ required: true, message: 'Please select city' }]}
                            >
                                <Select
                                    showSearch={true}
                                    placeholder='Select City'
                                    loading={isCitiesLoading}
                                    disabled={!stateId}
                                    options={cityList}
                                />
                            </Form.Item>
                        </Col>
                        <Col lg={8}>
                            <Form.Item
                                label="Pincode"
                                name="pincode"
                                rules={[
                                    { required: true, message: 'Please enter pincode' },
                                    {
                                        message: "Enter valid 6-digit pincode",
                                        pattern: InputValidatorRegex.PINCODE
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="Enter pincode"
                                    maxLength={6}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, '');
                                        locationFormProps.form?.setFieldsValue({ pincode: value });
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </Space>
    );
};
